import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Twitter, Linkedin, Youtube, MessageCircle, Navigation, ArrowUpRight } from "lucide-react";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { Contact } from "@/components/sections/Contact";
import { BRAND } from "@/data/content";
import { COUNTRIES_COUNT } from "@/lib/siteStats";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};
const TEL = BRAND.phone.replace(/[^0-9+]/g, "");
const WA = `https://wa.me/${BRAND.whatsapp.replace(/[^0-9]/g, "")}`;
const MAP_Q = encodeURIComponent("St. Andrews College, Golghar, Gorakhpur, Uttar Pradesh 273001");
const DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${MAP_Q}`;

const QUICK = [
  { icon: Phone, t: "Call us", v: BRAND.phone, href: `tel:${TEL}`, id: "call" },
  { icon: MessageCircle, t: "WhatsApp", v: "Chat with a travel expert", href: WA, id: "whatsapp", ext: true },
  { icon: Mail, t: "Email", v: BRAND.email, href: `mailto:${BRAND.email}`, id: "email" },
];

const SOCIALS = [
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/RoyaltoursGorakhpur" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/royal.holidayz" },
  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@royalholidayspvt" },
  { icon: Twitter, label: "Twitter", href: "https://x.com/RoyalTourTrave3" },
  { icon: Linkedin, label: "LinkedIn", href: "https://in.linkedin.com/in/royal-holidays-507020234" },
];

const Hero = () => (
  <section className="relative min-h-[52vh] flex items-end overflow-hidden" data-testid="contact-hero-section">
    <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "easeOut" }} src="/assets/media/company/office-shelf.jpg" alt="Royal Tours Group Head Office, Gorakhpur" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545]/95 via-[#0b2545]/80 to-[#0b2545]/30" />
    <div className="relative max-w-[1280px] w-full mx-auto px-4 lg:px-6 pt-24 pb-28 text-white">
      <motion.div variants={fadeUp} initial="hidden" animate="show" className="text-[var(--yellow)] font-semibold text-[11px] tracking-[0.35em] mb-4">HEAD OFFICE · GORAKHPUR</motion.div>
      <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1} className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="contact-title">Let's plan your <span className="text-[var(--yellow)]">next journey</span></motion.h1>
      <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-4 text-white/80 max-w-xl">Visit us, call us or drop a message — a travel expert replies within working hours, for holidays across India and {COUNTRIES_COUNT} countries.</motion.p>
    </div>
  </section>
);

const QuickActions = () => (
  <div className="relative z-10 -mt-14 max-w-[1280px] mx-auto px-4 lg:px-6 grid sm:grid-cols-3 gap-4">
    {QUICK.map((q, i) => (
      <motion.a key={q.id} href={q.href} {...(q.ext ? { target: "_blank", rel: "noreferrer" } : {})} variants={fadeUp} custom={i} initial="hidden" animate="show" data-testid={`contact-quick-${q.id}`}
        className="group bg-white rounded-xl p-5 shadow-[0_20px_40px_-20px_rgba(11,37,69,0.35)] border border-gray-100 flex items-center gap-4 hover:-translate-y-1 transition-transform duration-300">
        <span className="w-12 h-12 rounded-full bg-[var(--yellow)] text-[var(--navy)] flex items-center justify-center shrink-0 group-hover:bg-[var(--navy)] group-hover:text-[var(--yellow)] transition-colors"><q.icon className="w-5 h-5" /></span>
        <span className="min-w-0">
          <span className="block text-[11px] uppercase tracking-widest text-gray-500">{q.t}</span>
          <span className="block font-semibold text-[var(--navy)] truncate">{q.v}</span>
        </span>
        <ArrowUpRight className="w-4 h-4 ml-auto text-gray-300 group-hover:text-[var(--red)] transition-colors" />
      </motion.a>
    ))}
  </div>
);

const HeadOffice = () => (
  <section className="py-16 md:py-20 bg-white">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-0 rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5" data-testid="contact-head-office-card">
      <div className="lg:col-span-7 relative min-h-[300px]">
        <img src="/assets/media/company/office-reception.jpg" alt="Reception at the Royal Tours Group Head Office" className="absolute inset-0 w-full h-full object-cover" />
        <span className="absolute top-4 left-4 bg-white/95 text-[var(--navy)] text-[11px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full">Walk-ins welcome</span>
      </div>
      <div className="lg:col-span-5 bg-[var(--navy)] text-white p-8 md:p-10">
        <div className="text-[var(--yellow)] text-[11px] font-semibold tracking-[0.3em] uppercase">Head Office</div>
        <h2 className="font-serif-display text-3xl mt-2">{BRAND.name}</h2>
        <div className="mt-6 space-y-5 text-[14px]">
          <div className="flex gap-3"><MapPin className="w-5 h-5 text-[var(--yellow)] shrink-0 mt-0.5" /><span className="text-white/85 leading-relaxed" data-testid="head-office-address">{BRAND.address}</span></div>
          <div className="flex gap-3"><Clock className="w-5 h-5 text-[var(--yellow)] shrink-0 mt-0.5" />
            <div className="w-full text-white/85" data-testid="opening-hours">
              <div className="flex justify-between gap-4"><span>Monday – Saturday</span><span className="text-[var(--yellow)]">10:00 AM – 08:00 PM</span></div>
              <div className="flex justify-between gap-4 mt-1"><span>Sunday</span><span className="text-white/50">Closed</span></div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href={DIRECTIONS} target="_blank" rel="noreferrer" data-testid="contact-directions-btn" className="inline-flex items-center gap-2 bg-[var(--yellow)] text-[var(--navy)] font-semibold text-[13px] px-5 py-2.5 rounded-full hover:bg-white transition-colors"><Navigation className="w-4 h-4" /> Get directions</a>
          <a href={`tel:${TEL}`} data-testid="contact-call-btn" className="inline-flex items-center gap-2 border border-white/30 font-semibold text-[13px] px-5 py-2.5 rounded-full hover:bg-white hover:text-[var(--navy)] transition-colors"><Phone className="w-4 h-4" /> Call now</a>
        </div>
      </div>
    </div>
  </section>
);

const MapAndSocials = () => (
  <section className="pb-20 bg-white">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 rounded-2xl overflow-hidden border border-gray-200 shadow-sm min-h-[380px]" data-testid="contact-map">
        <iframe title="Royal Tours Group Head Office map" src={`https://www.google.com/maps?q=${MAP_Q}&output=embed`} className="w-full h-full min-h-[380px]" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      </div>
      <div className="lg:col-span-4 rounded-2xl bg-[var(--soft)] border border-gray-200 p-8 flex flex-col">
        <div className="text-[var(--red)] font-semibold text-[11px] tracking-[0.25em] uppercase">Stay connected</div>
        <h2 className="font-serif-display text-3xl mt-2">Follow the journey</h2>
        <p className="text-gray-600 text-sm mt-3">Travel reels, new departures and guest stories every week.</p>
        <div className="mt-6 grid grid-cols-5 gap-2">
          {SOCIALS.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`social-link-${s.label.toLowerCase()}`}
              className="aspect-square rounded-xl bg-white border border-gray-200 text-[var(--navy)] flex items-center justify-center hover:bg-[var(--navy)] hover:text-[var(--yellow)] transition-colors">
              <s.icon className="w-5 h-5" />
            </a>
          ))}
        </div>
        <div className="mt-auto pt-6"><img src="/assets/media/company/team-women.jpg" alt="Royal Tours Group holiday specialists" loading="lazy" className="rounded-xl w-full aspect-[16/9] object-cover" /></div>
      </div>
    </div>
  </section>
);

export default function ContactUs() {
  return (
    <main data-testid="contact-page" className="bg-white text-[var(--navy)] overflow-x-hidden">
      <Header />
      <Hero />
      <QuickActions />
      <HeadOffice />
      <Contact />
      <MapAndSocials />
      <Footer />
      <FloatingWidgets />
    </main>
  );
}
